import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import { InfoIcon } from "../assets/icons/icons";
import OnboardingModal from "../components/OnboardingModal";
import { trackGoal } from "../utils/datafast";
import { clearCheckoutTracking, readCheckoutTracking } from "../utils/stripe";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [name, setName] = useState("there");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [stats, setStats] = useState([
    { label: "Active", value: 0, tone: "green" },
    { label: "Pending", value: 0, tone: "amber" },
    { label: "Expiring Soon", value: 0, tone: "orange" },
    { label: "Issues", value: 0, tone: "red" },
  ]);
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");
  const [secureLinks, setSecureLinks] = useState([]);
  const [secureLabel, setSecureLabel] = useState("");
  const [secureUrl, setSecureUrl] = useState("");
  const [secureExpiresAt, setSecureExpiresAt] = useState("");
  const [secureLoading, setSecureLoading] = useState(false);
  const [secureError, setSecureError] = useState("");
  const [secureNotice, setSecureNotice] = useState("");
  const [copiedLinkId, setCopiedLinkId] = useState("");
  const [showSecureTip, setShowSecureTip] = useState(false);
  const [secureHistoryOpen, setSecureHistoryOpen] = useState(false);
  const [secureActionBusyId, setSecureActionBusyId] = useState("");
  const [trustSessions, setTrustSessions] = useState([]);
  const [trustLoading, setTrustLoading] = useState(false);
  const [trustError, setTrustError] = useState("");
  const [trustActionBusyId, setTrustActionBusyId] = useState("");
  const [trustHistoryOpen, setTrustHistoryOpen] = useState(false);
  const checkoutTrackedRef = useRef(false);

  const effectiveTier = useMemo(() => {
    const tier = String(subscriptionTier || "").toLowerCase();
    const status = String(subscriptionStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite") return tier;
    if (status === "pro" || status === "elite") return status;
    if (status === "active" || status === "trialing") return "pro";
    return "free";
  }, [subscriptionTier, subscriptionStatus]);

  const isElite = effectiveTier === "elite";
  const isProOrAbove = effectiveTier === "pro" || effectiveTier === "elite";
  const trustActiveSessions = useMemo(
    () => (trustSessions || []).filter((session) => session?.status !== "revoked"),
    [trustSessions]
  );
  const getSecureLinkState = useCallback((link) => {
    const isRevoked = !!link?.revoked_at;
    const isExpired = !!link?.expires_at && new Date(link.expires_at) < new Date();
    const isInactive = isRevoked || isExpired;
    return {
      isRevoked,
      isExpired,
      isInactive,
      statusLabel: isRevoked ? "Revoked" : isExpired ? "Expired" : "Active",
      statusClass: isInactive ? "muted" : "success",
    };
  }, []);
  const secureActiveLinks = useMemo(
    () => (secureLinks || []).filter((link) => !getSecureLinkState(link).isInactive),
    [getSecureLinkState, secureLinks]
  );
  const secureInactiveLinks = useMemo(
    () => (secureLinks || []).filter((link) => getSecureLinkState(link).isInactive),
    [getSecureLinkState, secureLinks]
  );

  const getAccessToken = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token || "";
  }, []);

  const loadSecureLinks = useCallback(async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/secure-links`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSecureLinks(Array.isArray(data?.links) ? data.links : []);
    } catch (_err) {
      // ignore
    }
  }, [getAccessToken]);

  const loadTrustSessions = useCallback(async () => {
    if (!isProOrAbove) {
      setTrustSessions([]);
      setTrustError("");
      return;
    }
    const accessToken = await getAccessToken();
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/trust-sessions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        setTrustError(payload?.error || "Unable to load trust sessions.");
        return;
      }
      const data = await res.json();
      setTrustSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (_err) {
      setTrustError("Unable to load trust sessions.");
    }
  }, [getAccessToken, isProOrAbove]);

  useEffect(() => {
    const fetchData = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name,last_name,subscription_tier,subscription_status")
        .eq("id", userId)
        .maybeSingle();

      if (profile) {
        const first = profile.first_name || "";
        setName(first || "there");
        setSubscriptionTier(profile.subscription_tier || "free");
        setSubscriptionStatus(profile.subscription_status || "inactive");
        const incomplete = !profile.first_name || !profile.last_name;
        if (incomplete) setShowOnboarding(true);
      }
      if (!profile) setShowOnboarding(true);

      const { data: licenses } = await supabase
        .from("licenses")
        .select("status,expires_on")
        .eq("removed", false);

      if (licenses) {
        const now = new Date();
        let active = 0;
        let pending = 0;
        let expiring = 0;
        let issues = 0;
        licenses.forEach((lic) => {
          const status = (lic.status || "").toLowerCase();
          if (status === "active" || status === "verified") active += 1;
          else if (status === "pending") pending += 1;
          else if (status === "issues" || status === "flagged") issues += 1;

          if (lic.expires_on) {
            const exp = new Date(lic.expires_on);
            const diffDays = (exp - now) / (1000 * 60 * 60 * 24);
            if (diffDays > 0 && diffDays <= 30) expiring += 1;
          }
        });
        setStats([
          { label: "Active", value: active, tone: "green" },
          { label: "Pending", value: pending, tone: "amber" },
          { label: "Expiring Soon", value: expiring, tone: "orange" },
          { label: "Issues", value: issues, tone: "red" },
        ]);
      }

      await Promise.all([loadSecureLinks(), loadTrustSessions()]);
    };

    fetchData();
  }, [loadSecureLinks, loadTrustSessions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("checkout") !== "success") return;
    if (checkoutTrackedRef.current) return;
    checkoutTrackedRef.current = true;
    const intent = readCheckoutTracking();
    trackGoal("upgrade_success", intent?.plan ? { plan_type: intent.plan } : undefined);
    clearCheckoutTracking();
    params.delete("checkout");
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const handleCreateSecureLink = async () => {
    setSecureError("");
    setSecureNotice("");
    if (!secureUrl.trim()) {
      setSecureError("Enter a destination URL to continue.");
      return;
    }
    if (!isElite) {
      setSecureError("Secure links are available for Elite members.");
      return;
    }
    setSecureLoading(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setSecureError("Please sign in again.");
        setSecureLoading(false);
        return;
      }
      const expiresAt = secureExpiresAt ? new Date(secureExpiresAt).toISOString() : null;
      const res = await fetch(`${API_BASE}/api/secure-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          originalUrl: secureUrl.trim(),
          label: secureLabel.trim(),
          expiresAt,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSecureError(payload?.error || "Unable to create secure link.");
        setSecureLoading(false);
        return;
      }
      trackGoal("secure_link_generated", { link_type: "redirect" });
      setSecureLabel("");
      setSecureUrl("");
      setSecureExpiresAt("");
      setSecureNotice("Secure link created.");
      await loadSecureLinks();
    } catch (_err) {
      setSecureError("Unable to create secure link.");
    } finally {
      setSecureLoading(false);
    }
  };

  const handleCopySecureLink = async (link) => {
    if (!link?.secure_url) return;
    try {
      await navigator.clipboard.writeText(link.secure_url);
      setCopiedLinkId(link.id);
      setTimeout(() => setCopiedLinkId(""), 1500);
    } catch (_err) {
      // ignore
    }
  };

  const handleRevokeSecureLink = async (linkId) => {
    if (!linkId) return;
    setSecureError("");
    setSecureNotice("");
    const accessToken = await getAccessToken();
    if (!accessToken) {
      setSecureError("Please sign in again.");
      return;
    }
    setSecureActionBusyId(linkId);
    try {
      const res = await fetch(`${API_BASE}/api/secure-links/${linkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSecureError(payload?.error || "Unable to revoke secure link.");
        return;
      }
      setSecureNotice("Secure link revoked.");
      await loadSecureLinks();
    } catch (_err) {
      setSecureError("Unable to revoke secure link.");
    } finally {
      setSecureActionBusyId("");
    }
  };

  const handleCreateTrustSession = async () => {
    setTrustError("");
    if (!isProOrAbove) {
      setTrustError("Trust Sessions are available on Pro and Elite plans.");
      return;
    }
    setTrustLoading(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setTrustError("Please sign in again.");
        setTrustLoading(false);
        return;
      }
      const res = await fetch(`${API_BASE}/api/trust-sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTrustError(payload?.error || "Unable to create trust session.");
        setTrustLoading(false);
        return;
      }
      trackGoal("trust_session_created");
      await loadTrustSessions();
    } catch (_err) {
      setTrustError("Unable to create trust session.");
    } finally {
      setTrustLoading(false);
    }
  };

  const handleRevokeTrustSession = async (sessionId) => {
    const accessToken = await getAccessToken();
    if (!accessToken || !sessionId) return;
    setTrustActionBusyId(sessionId);
    try {
      await fetch(`${API_BASE}/api/trust-sessions/${sessionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await loadTrustSessions();
    } catch (_err) {
      // ignore
    } finally {
      setTrustActionBusyId("");
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "Never";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "Never";
    return dt.toLocaleString();
  };

  const renderSecureLinkCard = (link, keyPrefix = "") => {
    const { isInactive, statusClass, statusLabel } = getSecureLinkState(link);
    const busy = secureActionBusyId === link.id;
    return (
      <div key={`${keyPrefix}${link.id}`} className="license-card secure-link-card">
        <div className="secure-link-header">
          <div>
            <h4>{link.label || "Secure link"}</h4>
            <p className="muted small">{link.original_url}</p>
          </div>
          <span className={`badge ${statusClass}`} style={{ textTransform: "capitalize" }}>
            {statusLabel}
          </span>
        </div>
        <div className="secure-link-meta">
          <span>Created: {formatDateTime(link.created_at)}</span>
          <span>Expires: {formatDateTime(link.expires_at)}</span>
          <span>Clicks: {link.redirect_count || 0}</span>
        </div>
        {link.secure_url && <p className="secure-link-url">{link.secure_url}</p>}
        <div className="secure-link-actions">
          <button className="ghost" type="button" onClick={() => handleCopySecureLink(link)} disabled={busy}>
            {copiedLinkId === link.id ? "Copied" : "Copy Link"}
          </button>
          {!isInactive && (
            <button className="danger" type="button" onClick={() => handleRevokeSecureLink(link.id)} disabled={busy}>
              {busy ? "Revoking..." : "Revoke Link"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      {showOnboarding && (
        <OnboardingModal
          onClose={() => setShowOnboarding(false)}
          onComplete={() => {
            setShowOnboarding(false);
          }}
        />
      )}
      <div className="page-header">
        <div>
          <p className="eyebrow">Manage your licenses and verification status</p>
          <h1 data-page-heading="true">Welcome back, {name}</h1>
        </div>
      </div>

      <div className="stat-grid">
        {stats.map((item) => (
          <div key={item.label} className={`stat-card ${item.tone}`}>
            <div className="stat-value">{item.value}</div>
            <div className="stat-label">{item.label}</div>
          </div>
        ))}
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>Quick Actions</h3>
        </div>
        <div className="button-row">
          <button type="button" className="primary dark" onClick={() => (window.location.href = "/licenses")}>
            Add License
          </button>
          <button type="button" className="ghost" onClick={() => (window.location.href = "/verification")}>
            View Verification Status
          </button>
          <button type="button" className="ghost" onClick={() => (window.location.href = "/leads")}>
            View Leads
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>Trust Session</h3>
          {isProOrAbove && (
            <button
              type="button"
              className="panel-options-btn"
              aria-label="Open trust session history"
              onClick={() => setTrustHistoryOpen(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="5" r="1.75" />
                <circle cx="12" cy="12" r="1.75" />
                <circle cx="12" cy="19" r="1.75" />
              </svg>
            </button>
          )}
        </div>
        <p className="muted" style={{ marginTop: 6 }}>
          Start a one-time, short-lived viewer link so consumers can watch your screen share and optional camera in-browser.
          This is view-only.
        </p>
        {isProOrAbove ? (
          <>
            {trustError && <p className="error-message">{trustError}</p>}
            <div className="button-row" style={{ marginTop: 12 }}>
              <button className="primary" type="button" onClick={handleCreateTrustSession} disabled={trustLoading}>
                {trustLoading ? "Creating..." : "Create Trust Session"}
              </button>
              <button className="ghost" type="button" onClick={loadTrustSessions}>
                Refresh Sessions
              </button>
            </div>
            <div className="license-list" style={{ marginTop: 16 }}>
              {trustActiveSessions.map((session) => {
                const statusClass =
                  session.status === "live"
                    ? "success"
                    : session.status === "ready"
                      ? "amber"
                      : "muted";
                const hasBroadcastStarted = !!session.host_joined_at;
                const busy = trustActionBusyId === session.id;
                return (
                  <div key={session.id} className="license-card trust-session-card">
                    <div className="secure-link-header">
                      <div>
                        <h4>Trust session</h4>
                      </div>
                      <span className={`badge ${statusClass}`} style={{ textTransform: "capitalize" }}>
                        {session.status}
                      </span>
                    </div>
                    <div className="secure-link-meta">
                      <span>
                        <span className="muted small" style={{ marginBottom: 6, display: "inline-block" }}>
                          Created: {formatDateTime(session.created_at)} &nbsp; Expires: {formatDateTime(session.expires_at)}
                        </span>
                      </span>
                    </div>
                    <p className="muted small">
                      {hasBroadcastStarted ? session.session_url : "Link will appear after broadcast starts."}
                    </p>
                    <div className="secure-link-actions">
                      {session.status === "ready" || session.status === "live" ? (
                        <button className="primary dark" type="button" onClick={() => navigate(`/trust-session/${session.id}`)}>
                          Host Studio
                        </button>
                      ) : null}
                      {session.status !== "revoked" ? (
                        <button className="ghost" type="button" onClick={() => handleRevokeTrustSession(session.id)} disabled={busy}>
                          {busy ? "Working..." : "Revoke"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {trustActiveSessions.length === 0 && (
                <p className="muted">
                  No active trust sessions. Open options to view full history.
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 12 }}>
              Trust Session is included with Pro and Elite plans.
            </p>
            <div className="button-row" style={{ marginTop: 12 }}>
              <button
                className="primary"
                type="button"
                onClick={() => {
                  trackGoal("upsell_interacted", {
                    upsell_id: "trust_sessions_upsell",
                    action: "view_pro_features",
                  });
                  window.location.href = "/plans";
                }}
              >
                View Pro Features
              </button>
            </div>
          </>
        )}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3>Secure Link Generator</h3>
            <span className="info-tooltip-wrap">
              <button
                type="button"
                className="info-tooltip-btn"
                aria-label="About secure links"
                aria-expanded={showSecureTip}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSecureTip((prev) => !prev);
                }}
              >
                <InfoIcon className="info-icon" />
              </button>
              <span role="tooltip" className={`info-tooltip ${showSecureTip ? "open" : ""}`}>
                Secure links help prevent spoofing by routing users through your verified ClearBadge profile before redirecting.
              </span>
            </span>
          </div>
          {isElite && (
            <button
              type="button"
              className="panel-options-btn"
              aria-label="Open secure link history"
              onClick={() => setSecureHistoryOpen(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="5" r="1.75" />
                <circle cx="12" cy="12" r="1.75" />
                <circle cx="12" cy="19" r="1.75" />
              </svg>
            </button>
          )}
        </div>
        {isElite ? (
          <>
            <p className="muted" style={{ marginTop: 6 }}>
              Create a secure ClearBadge link that redirects to your existing document, contract, or payment request.
            </p>
            <div className="form-row dual" style={{ marginTop: 12 }}>
              <div>
                <label htmlFor="secure-link-label" style={{ marginBottom: "0.5rem", display: "block" }}>
                  Label (optional)
                </label>
                <input
                  id="secure-link-label"
                  type="text"
                  value={secureLabel}
                  onChange={(e) => setSecureLabel(e.target.value)}
                  placeholder="e.g DocuSign contract"
                />
              </div>
              <div>
                <label htmlFor="secure-link-expires" style={{ marginBottom: "0.5rem", display: "block" }}>
                  Expires On (optional)
                </label>
                <input
                  id="secure-link-expires"
                  type="datetime-local"
                  value={secureExpiresAt}
                  onChange={(e) => setSecureExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <div className="form-row" style={{ marginTop: 12 }}>
              <label htmlFor="secure-link-destination">Destination URL</label>
              <input
                id="secure-link-destination"
                type="url"
                value={secureUrl}
                onChange={(e) => setSecureUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            {secureError && (
              <p className="error-message" style={{ marginTop: 8 }}>
                {secureError}
              </p>
            )}
            {secureNotice && (
              <p className="muted" style={{ marginTop: 8 }}>
                {secureNotice}
              </p>
            )}
            <div className="button-row" style={{ marginTop: 12 }}>
              <button className="primary" type="button" onClick={handleCreateSecureLink} disabled={secureLoading}>
                {secureLoading ? "Creating..." : "Create Secure Link"}
              </button>
            </div>

            <div className="license-list" style={{ marginTop: 16 }}>
              {secureActiveLinks.map((link) => renderSecureLinkCard(link))}
              {secureActiveLinks.length === 0 && (
                <p className="muted">
                  No active secure links. Open options to view expired and revoked links.
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 6 }}>
              Send contracts, invoices, and requests through your verified ClearBadge profile to build trust and prevent
              spoofing.
              <br />
              <br />
              Included with the Elite plan.
            </p>
            <div className="button-row" style={{ marginTop: 12 }}>
              <button
                className="primary"
                type="button"
                onClick={() => {
                  trackGoal("upsell_interacted", {
                    upsell_id: "secure_links_upsell",
                    action: "view_elite_features",
                  });
                  window.location.href = "/plans";
                }}
              >
                View Elite Features
              </button>
            </div>
          </>
        )}
      </div>

      {isProOrAbove && trustHistoryOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setTrustHistoryOpen(false);
          }}
        >
          <div className="modal history-panel-modal" role="dialog" aria-modal="true" aria-labelledby="trust-history-title">
            <div className="modal-header">
              <div>
                <h3 id="trust-history-title">Trust Session History</h3>
                <p className="muted small" style={{ marginTop: 4 }}>
                  Includes all sessions, including revoked.
                </p>
              </div>
              <button
                type="button"
                className="icon-btn history-modal-close"
                aria-label="Close trust session history"
                onClick={() => setTrustHistoryOpen(false)}
              >
                x
              </button>
            </div>

            <div className="license-list">
              {trustSessions.map((session) => {
                const statusClass =
                  session.status === "live"
                    ? "success"
                    : session.status === "ready"
                      ? "amber"
                      : "muted";
                const hasBroadcastStarted = !!session.host_joined_at;
                return (
                  <div key={`history-${session.id}`} className="license-card trust-session-card history-panel-card">
                    <div className="secure-link-header">
                      <div>
                        <h4>Trust session</h4>
                      </div>
                      <span className={`badge ${statusClass}`} style={{ textTransform: "capitalize" }}>
                        {session.status}
                      </span>
                    </div>
                    <div className="secure-link-meta">
                      <span>
                        <span className="muted small" style={{ marginBottom: 6, display: "inline-block" }}>
                          Created: {formatDateTime(session.created_at)} &nbsp; Expires: {formatDateTime(session.expires_at)}
                        </span>
                      </span>
                    </div>
                    <p className="muted small">
                      {hasBroadcastStarted ? session.session_url : "Link hidden until broadcast starts."}
                    </p>
                  </div>
                );
              })}
              {trustSessions.length === 0 && <p className="muted">No trust session history yet.</p>}
            </div>
          </div>
        </div>
      )}

      {isElite && secureHistoryOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSecureHistoryOpen(false);
          }}
        >
          <div className="modal history-panel-modal" role="dialog" aria-modal="true" aria-labelledby="secure-history-title">
            <div className="modal-header">
              <div>
                <h3 id="secure-history-title">Secure Link History</h3>
                <p className="muted small" style={{ marginTop: 4 }}>
                  Includes expired and revoked links so active links stay in the main section.
                </p>
              </div>
              <button
                type="button"
                className="icon-btn history-modal-close"
                aria-label="Close secure link history"
                onClick={() => setSecureHistoryOpen(false)}
              >
                x
              </button>
            </div>

            <div className="license-list">
              {secureInactiveLinks.map((link) => renderSecureLinkCard(link, "history-"))}
              {secureInactiveLinks.length === 0 && <p className="muted">No expired or revoked secure links yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
